import { isFieldPersisted, } from '@rammetec/data-access';
import { AUDIT_FIELD_NAMES } from '../descriptors/_auditFields.js';
import { SERVICE_FILLED_NAMES } from '../validators/serviceFilledFields.js';
const SCALAR_FIELD_TYPES = new Set([
    'string',
    'number',
    'boolean',
    'date',
    'enum',
]);
const SKIP_FIELD_NAMES = new Set([
    'id',
    ...AUDIT_FIELD_NAMES,
    ...SERVICE_FILLED_NAMES,
]);
/** Whether a descriptor field is exposed as a template scalar (D1). */
export function isExpandableScalarField(field, options) {
    if (field.virtual === true)
        return false;
    if (SKIP_FIELD_NAMES.has(field.name))
        return false;
    if (options.fkFieldNames.has(field.name))
        return false;
    if (!SCALAR_FIELD_TYPES.has(field.type))
        return false;
    if (!isFieldPersisted(field, options.activeGroups))
        return false;
    if (field.audit === 'names-only')
        return false;
    if (options.excludeLocal?.has(field.name))
        return false;
    if (options.include && options.include.length > 0 && !options.include.includes(field.name)) {
        return false;
    }
    return true;
}
/** Scalar field names from a descriptor (same selection as the schema plan). */
export function getExpandableScalarFieldNames(descriptor, activeGroups, include, excludeLocal) {
    const fkFieldNames = new Set((descriptor.foreignKeys ?? []).map((fk) => fk.fieldName));
    const names = [];
    for (const field of descriptor.fields) {
        if (!isExpandableScalarField(field, {
            fkFieldNames,
            activeGroups,
            include,
            excludeLocal,
        })) {
            continue;
        }
        names.push(field.name);
    }
    return names;
}
/** Copies allowed scalar fields from a record; omits undefined values. */
export function pickRecordFields(record, fieldNames) {
    const result = {};
    for (const name of fieldNames) {
        const value = record[name];
        if (value !== undefined) {
            result[name] = value;
        }
    }
    return result;
}
/** Sets a nested value on a plain object using dot-path notation. */
export function setNestedValue(target, path, value) {
    const parts = path.split('.');
    let cursor = target;
    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        const next = cursor[part];
        if (next === undefined || typeof next !== 'object' || next === null || Array.isArray(next)) {
            cursor[part] = {};
        }
        cursor = cursor[part];
    }
    cursor[parts[parts.length - 1]] = value;
}
/** Object key for a FK field (`lotId` → `lot`; no suffix → keep name). */
export function relationKeyFromFieldName(fieldName) {
    return fieldName.endsWith('Id') ? fieldName.slice(0, -2) : fieldName;
}
/**
 * Splits exclude paths for a relation child `childKey`.
 * - `entry === childKey` → whole relation omitted (`omit: true`)
 * - `'lot.notes'` with childKey `'lot'` → local `'notes'` on the child
 * - `'lot.variant.x'` → nested `'variant.x'` for further hops
 *
 * Undotted names are ignored here: they belong to the parent node (caller already
 * split them out before calling).
 */
export function splitExcludeForChild(exclude, childKey) {
    const local = new Set();
    const nested = [];
    if (!exclude)
        return { omit: false, local, nested };
    let omit = false;
    const prefix = `${childKey}.`;
    for (const entry of exclude) {
        if (entry === childKey) {
            omit = true;
            continue;
        }
        if (entry.startsWith(prefix)) {
            const rest = entry.slice(prefix.length);
            // `lot.notes` → local on lot; `lot.variant.x` → nested for further hops.
            if (rest.includes('.'))
                nested.push(rest);
            else
                local.add(rest);
        }
    }
    return { omit, local, nested };
}
/** True when the target can be loaded by id alone (not a subcollection). */
export function isLoadableByIdAlone(descriptor) {
    const keys = Object.keys(descriptor.pathPatternKeys ?? {});
    return keys.every((k) => k === 'orgId');
}
//# sourceMappingURL=schemaSourceFields.js.map